import { ReactElement, cloneElement, ReactNode } from 'react'
import { css, styled, CSS } from 'theme'
import { ColorProps } from '.'

export const TagCss = styled('span', {
  // Reset
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'flexShrink': 0,
  'WebkitTapHighlightColor': 'rgba(0,0,0,0)',
  'transitionButton': '$transition$easeOutQuint',
  'border': '1px solid $inputBorder',
  'p': '$space$4 $space$6',
  'backgroundColor': '$inputBg',
  'color': '$inputLabel',
  'cursor': 'auto',

  // Custom
  'fontSize': '$14',
  'lineHeight': '$lineHeight$20',
  'borderRadius': '$radii$sm',
  'fontWeight': '$medium',
  'maxHeight': 24,
  'svg': {
    'cursor': 'pointer',
    '&:hover': {
      path: {
        stroke: '$primaryBase',
      },
    },
  },
  '&:disabled': {
    backgroundColor: '$primaryDisabled',
    color: '$disabledText',
    cursor: 'not-allowed',
    borderColor: '$primaryDisabled',
  },

  'variants': {
    size: {
      sm: {
        borderRadius: '$radii$xs',
      },
      md: {
        borderRadius: '$radii$sm',
        maxHeight: 28,
      },
      lg: {
        borderRadius: '$radii$sm',
        p: '$space$8 $space$14',
        maxHeight: 36,
      },
      xl: {
        borderRadius: '$radii$sm',
        p: '$space$8 $space$14',
        fontSize: '$16',
        lineHeight: '$lineHeight$24',
        maxHeight: 36,
      },
      rounded: {
        borderRadius: '$radii$md',
        p: '$space$2 $space$10',
        maxHeight: 28,
      },
      roundedSmall: {
        fontSize: '$10',
        lineHeight: '$lineHeight$18',
        borderRadius: '$radii$md',
        p: '0 $space$10',
        maxHeight: 28,
      },
    },
    color: {
      primary: {
        'backgroundColor': '$primary50',
        'color': '$primaryBase',
        'borderColor': '$primary200',
        '&:hover': {
          borderColor: '$primary400',
        },
      },
      secondary: {
        backgroundColor: 'transparent',
        color: '$gray600',
        borderColor: '$gray700',
      },
      gray: {
        backgroundColor: '$gray100',
        color: '$titleGray',
        borderColor: '$gray100',
      },
      warning: {
        backgroundColor: '$warning50',
        color: '$warning700',
        borderColor: '$warning200',
      },
      error: {
        backgroundColor: '$error50',
        color: '$error700',
        borderColor: '$error200',
      },
      success: {
        backgroundColor: '$success50',
        color: '$success700',
        borderColor: '$success200',
      },
      transparent: {
        backgroundColor: '$inputBg',
        color: '$inputLabel',
        borderColor: '$inputBorder',
      },
      black: {},
      white: {},
      disabled: {},
    },
  },
  'defaultVariants': {
    size: 'sm',
    color: 'transparent',
  },
})

const TagCircleVariants = {
  color: {
    primary: {
      background: '$primaryBase',
    },
    secondary: {
      background: '$secondaryBase',
    },
    warning: {
      background: '$warning500',
    },
    error: {
      background: '$error500',
    },
    success: {
      background: '$success500',
    },
    gray: {
      background: '$text',
    },
    white: {
      background: '$whiteBase',
    },
    black: {
      background: '$blackBase',
    },
  },
}

export const TagCircle = styled('span', {
  width: 7,
  height: 7,
  borderRadius: '$radii$rounded',
  variants: {
    color: TagCircleVariants.color,
  },
  defaultVariants: {
    color: 'primary',
  },
})

interface TagProps {
  color?: ColorProps
  size?: 'sm' | 'md' | 'lg' | 'rounded' | 'roundedSmall'
  css?: CSS
  startIcon?: ReactNode
  endIcon?: ReactNode
  children?: ReactNode
  onClick?: (arg: any) => void
}

export const Tag = ({
  startIcon,
  endIcon,
  children,
  onClick,
  ...rest
}: TagProps) => {
  const iconStyle = css({
    ...(startIcon && { marginRight: '$space$8' }),
    ...(endIcon && { marginLeft: '$space$8' }),
  })

  return (
    <TagCss {...rest}>
      {startIcon &&
        cloneElement(startIcon as ReactElement, {
          className: iconStyle.toString(),
          onClick: onClick,
        })}
      {children}
      {endIcon &&
        cloneElement(endIcon as ReactElement, {
          className: iconStyle.toString(),
          onClick: onClick,
        })}
    </TagCss>
  )
}
